import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Antisaccade — FTUE demo (3 essais pédagogiques).
 *
 *   E1 : cue droite → réponse correcte = gauche (le piège oculomoteur).
 *   E2 : cue gauche → réponse correcte = droite.
 *   E3 : tempo réel, configuration proche de la session.
 *
 * Pédagogie cardinale : « Le piège : ton oeil veut suivre le cue. Résiste. »
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { oppositeSide } from './types';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'antisaccade.demo.step1_title',
        hintKey: 'antisaccade.demo.step1_hint',
        cueSide: 'right',
        soaMs: 400,
        cueDurationMs: 220,
        targetDurationMs: 220,
        targetGlyph: '<',
    },
    {
        idx: 2,
        titleKey: 'antisaccade.demo.step2_title',
        hintKey: 'antisaccade.demo.step2_hint',
        cueSide: 'left',
        soaMs: 320,
        cueDurationMs: 200,
        targetDurationMs: 200,
        targetGlyph: '>',
    },
    {
        idx: 3,
        titleKey: 'antisaccade.demo.step3_title',
        hintKey: 'antisaccade.demo.step3_hint',
        cueSide: 'right',
        soaMs: 220,
        cueDurationMs: 180,
        targetDurationMs: 180,
        targetGlyph: '<',
    },
];
export function Demo({ onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('fixation');
    const [picked, setPicked] = useState(null);
    const timers = useRef([]);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const targetSide = oppositeSide(step.cueSide);
    function clearAll() {
        for (const t of timers.current)
            clearTimeout(t);
        timers.current = [];
    }
    useEffect(() => {
        clearAll();
        setPhase('fixation');
        setPicked(null);
        timers.current.push(setTimeout(() => {
            setPhase('cue');
            timers.current.push(setTimeout(() => {
                setPhase('gap');
                timers.current.push(setTimeout(() => {
                    setPhase('target');
                    timers.current.push(setTimeout(() => {
                        setPhase('awaiting');
                    }, step.targetDurationMs));
                    timers.current.push(setTimeout(() => {
                        setPhase('feedback');
                    }, 2000));
                }, step.soaMs));
            }, step.cueDurationMs));
        }, 500));
        return () => clearAll();
    }, [stepIdx, step.cueDurationMs, step.soaMs, step.targetDurationMs]);
    const correct = (() => {
        if (phase !== 'feedback')
            return null;
        return picked === targetSide;
    })();
    const handlePick = (s) => {
        if (phase !== 'awaiting' && phase !== 'target')
            return;
        clearAll();
        setPicked(s);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    const feedbackLabel = (() => {
        if (correct)
            return '✓';
        if (picked === null)
            return `✗ ${t('antisaccade.feedback.timeout')}`;
        return `✗ ${t('antisaccade.feedback.followed_cue')}`;
    })();
    const showCue = phase === 'cue';
    const showTarget = phase === 'target';
    const showButtons = phase === 'awaiting' || phase === 'target';
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('antisaccade.demo.eyebrow') }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "relative flex h-[200px] w-full max-w-[320px] items-center justify-center", style: { background: '#1a1a1a' }, children: [phase === 'fixation' && (_jsx("span", { className: "font-display text-[36px] text-cyan-surface", children: "+" })), (phase === 'cue' || phase === 'gap' || phase === 'target' || phase === 'awaiting') && (_jsx("span", { className: "absolute font-display text-[24px] text-cyan-surface/40", children: "+" })), showCue && (_jsx("span", { className: [
                            'absolute font-display text-[36px] text-cyan-media',
                            step.cueSide === 'left' ? 'left-s4' : 'right-s4',
                        ].join(' '), "aria-hidden": "true", children: "\u25CF" })), showTarget && (_jsx("span", { className: [
                            'absolute font-display text-[44px] text-cyan-surface',
                            targetSide === 'left' ? 'left-s4' : 'right-s4',
                        ].join(' '), children: step.targetGlyph })), phase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[20px]',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: feedbackLabel }))] }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [showButtons && (_jsxs("div", { className: "flex justify-center gap-s3", children: [_jsx("button", { type: "button", onClick: () => handlePick('left'), "aria-label": t('antisaccade.tap_left_aria'), className: "flex h-[60px] w-[60px] items-center justify-center rounded-full bg-cyan-surface text-[28px] text-cyan-text border border-cyan-border hover:scale-110 hover:bg-cyan-network/15 active:scale-95", children: "\u2190" }), _jsx("button", { type: "button", onClick: () => handlePick('right'), "aria-label": t('antisaccade.tap_right_aria'), className: "flex h-[60px] w-[60px] items-center justify-center rounded-full bg-cyan-surface text-[28px] text-cyan-text border border-cyan-border hover:scale-110 hover:bg-cyan-network/15 active:scale-95", children: "\u2192" })] })), phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map